import { Link } from 'react-router-dom';
import PlatformVersionBadge from '@/components/feature/PlatformVersionBadge';
import type { ZifekUser } from '@/hooks/useAuth';

interface Props {
  open: boolean;
  user: ZifekUser;
  brandName: string;
  activeTab: string;
  fileCount: number;
  upcomingCount: number;
  onClose: () => void;
  onLogout: () => void;
}

interface Item {
  id: string;
  icon: string;
  label: string;
  count?: number;
}

const ACCOUNT_ITEMS: Item[] = [
  { id: 'overview', icon: 'ri-dashboard-line', label: 'Tableau de bord' },
  { id: 'etudes', icon: 'ri-book-open-line', label: "Mon espace d'études" },
  { id: 'appstore', icon: 'ri-store-3-line', label: 'App Store' },
  { id: 'mes-applis', icon: 'ri-apps-2-line', label: 'Mes applications' },
  { id: 'profile', icon: 'ri-user-settings-line', label: 'Mon profil' },
  { id: 'store', icon: 'ri-store-2-line', label: 'Mes soldes magasin' },
  { id: 'wallet', icon: 'ri-wallet-3-line', label: 'Mon portefeuille' },
  { id: 'favorites', icon: 'ri-heart-3-line', label: 'Mes favoris' },
  { id: 'chat', icon: 'ri-robot-2-line', label: 'Chat IA' },
  { id: 'messages', icon: 'ri-chat-smile-3-line', label: 'Messages' },
  { id: 'orders', icon: 'ri-shopping-bag-3-line', label: 'Mes commandes' },
  { id: 'payments', icon: 'ri-receipt-line', label: 'Historique des paiements' },
  { id: 'besoins', icon: 'ri-hand-heart-line', label: 'Besoins' },
  { id: 'missions', icon: 'ri-megaphone-line', label: 'Missions' },
  { id: 'packs', icon: 'ri-luggage-cart-line', label: 'Mes packs' },
  { id: 'revenues', icon: 'ri-money-dollar-circle-line', label: 'Mes gains créateur' },
];

const SPACE_ITEMS: Item[] = [
  { id: 'outfits', icon: 'ri-shirt-line', label: 'Mon Dressing' },
  { id: 'closet', icon: 'ri-t-shirt-line', label: 'Mon Armoire' },
  { id: 'documents', icon: 'ri-file-text-line', label: 'Mes documents' },
  { id: 'files', icon: 'ri-folder-3-line', label: 'Mes fichiers' },
  { id: 'downloads', icon: 'ri-download-cloud-2-line', label: 'Mes téléchargements' },
];

/**
 * Tiroir de navigation du compte client (mobile / tablette étroite).
 * Toujours monté : il glisse depuis la gauche avec un fond sombre cliquable,
 * au-dessus de la barre du haut et de la barre inférieure, pour que la
 * navigation soit accessible en toutes circonstances.
 */
export default function ClientMobileDrawer({
  open,
  user,
  brandName,
  activeTab,
  fileCount,
  upcomingCount,
  onClose,
  onLogout,
}: Props) {
  const linkClass = (active: boolean) =>
    `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all cursor-pointer no-underline ${
      active
        ? 'bg-foreground-100/70 text-foreground-900'
        : 'text-foreground-500 hover:text-foreground-700 hover:bg-background-100'
    }`;

  const section = (text: string) => (
    <div className="px-4 mt-3 mb-1">
      <span className="text-[10px] font-semibold text-foreground-400 tracking-[0.12em] uppercase px-2">{text}</span>
    </div>
  );

  return (
    <div className={`md:hidden fixed inset-0 z-[60] ${open ? '' : 'pointer-events-none'}`}>
      <div
        className={`absolute inset-0 bg-foreground-950/45 transition-opacity duration-300 ${open ? 'opacity-100' : 'opacity-0'}`}
        onClick={onClose}
        aria-hidden="true"
      />
      <aside
        className={`absolute inset-y-0 left-0 w-[86%] max-w-[320px] bg-background-50 border-r border-background-200/60 flex flex-col transition-transform duration-300 ease-out ${
          open ? 'translate-x-0' : '-translate-x-full'
        }`}
        aria-label="Navigation du compte"
      >
        <div className="flex items-center justify-between gap-3 px-4 py-3 border-b border-background-200/60 flex-shrink-0">
          <span className="text-sm font-bold font-heading text-foreground-950">Mon compte</span>
          <button
            type="button"
            onClick={onClose}
            className="w-9 h-9 flex items-center justify-center rounded-full text-foreground-500 hover:bg-background-100 transition-colors cursor-pointer"
            aria-label="Fermer le menu"
          >
            <i className="ri-close-line text-lg"></i>
          </button>
        </div>

        <div className="px-4 pt-3 pb-2 flex-shrink-0">
          <div className="flex items-center gap-3 p-3 bg-background-50 border border-background-200/50 rounded-xl">
            <div className="w-10 h-10 rounded-full bg-foreground-100 flex items-center justify-center overflow-hidden flex-shrink-0">
              {user.image ? (
                <img src={user.image} alt="" className="w-full h-full object-cover" />
              ) : (
                <span className="text-foreground-600 font-bold text-sm">{user.name?.charAt(0)?.toUpperCase() || '?'}</span>
              )}
            </div>
            <div className="min-w-0">
              <p className="text-sm font-semibold text-foreground-900 truncate">{user.name}</p>
              <p className="text-[11px] text-foreground-500 truncate">@{user.user_name}</p>
            </div>
          </div>
        </div>

        <div className="flex-1 min-h-0 overflow-y-auto pb-4">
          {section('Compte')}
          <div className="px-2 flex flex-col gap-0.5">
            {ACCOUNT_ITEMS.map((item) => (
              <Link
                key={item.id}
                to={`/mon-compte/${item.id}`}
                onClick={onClose}
                className={linkClass(activeTab === item.id)}
              >
                <i className={`${item.icon} text-base`}></i>
                <span className="flex-1">{item.label}</span>
              </Link>
            ))}
          </div>

          {section('Espace')}
          <div className="px-2 flex flex-col gap-0.5">
            {SPACE_ITEMS.map((item) => (
              <Link
                key={item.id}
                to={`/mon-compte/${item.id}`}
                onClick={onClose}
                className={linkClass(activeTab === item.id)}
              >
                <i className={`${item.icon} text-base`}></i>
                <span className="flex-1">{item.label}</span>
                {item.id === 'files' && fileCount > 0 && (
                  <span className="text-[11px] text-foreground-400 bg-background-200/70 px-1.5 py-0.5 rounded-full">{fileCount}</span>
                )}
              </Link>
            ))}
          </div>

          {section('Agenda')}
          <div className="px-2 flex flex-col gap-0.5">
            <Link to="/mon-planning" onClick={onClose} className={linkClass(false)}>
              <i className="ri-calendar-2-line text-base"></i>
              <span className="flex-1">Planning semaine</span>
              {upcomingCount > 0 && (
                <span className="px-2 py-0.5 rounded-full bg-accent-100 text-accent-700 text-[10px] font-bold">{upcomingCount}</span>
              )}
            </Link>
            <Link to="/mon-compte/missions" onClick={onClose} className={linkClass(false)}>
              <i className="ri-fire-line text-base"></i>
              <span className="flex-1">Campagnes créateurs</span>
            </Link>
          </div>
        </div>

        <div className="shrink-0 px-4 py-3 border-t border-background-200/50">
          <Link
            to="/"
            onClick={onClose}
            className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-xs font-medium text-foreground-500 hover:text-foreground-700 hover:bg-background-100 transition-colors no-underline"
          >
            <i className="ri-home-4-line text-sm"></i>
            Accueil {brandName}
          </Link>
          <button
            type="button"
            onClick={onLogout}
            className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-xs font-medium text-foreground-400 hover:text-red-600 hover:bg-red-50/60 transition-colors cursor-pointer w-full mt-0.5"
          >
            <i className="ri-logout-box-line text-sm"></i>
            Déconnexion
          </button>
          <div className="pt-3 mt-1 flex justify-center">
            <PlatformVersionBadge />
          </div>
        </div>
      </aside>
    </div>
  );
}